/**
 * save/storage.ts — slot-based localStorage wrapper for v1 single-player saves.
 *
 * Per MULTIPLAYER §6.2 (v2M2): three named slots + one quicksave slot.
 * Layout in storage:
 *   su:save:slot:1     ─┐
 *   su:save:slot:2      ├─ JSON-encoded SaveBlob, one per occupied slot
 *   su:save:slot:3     ─┘
 *   su:save:quicksave   — JSON-encoded SaveBlob (or absent)
 *   su:save:index       — JSON-encoded SaveIndex; one row per occupied slot,
 *                         caches header metadata so the slot picker renders
 *                         without re-parsing every blob.
 *
 * Storage is injectable so tests can pass an in-memory adapter rather than
 * relying on a real localStorage. Default behaviour reads `globalThis.localStorage`
 * lazily so importing this module in a non-DOM context doesn't crash.
 */

import {
  type SaveBlob,
  type SaveBlobHeader,
} from './serialize';

/**
 * Stable slot identifier. Three named slots plus one quicksave. The named
 * slot ids stay stable across UI labels so a player can rename a save (v2M8
 * polish) without rotating its storage key.
 */
export type SaveSlotKey = 'slot:1' | 'slot:2' | 'slot:3' | 'quicksave';

export const ALL_SLOT_KEYS: readonly SaveSlotKey[] = [
  'slot:1',
  'slot:2',
  'slot:3',
  'quicksave',
];

/** Per-slot metadata returned by `listSaveSlots`. */
export interface SaveSlotEntry {
  key: SaveSlotKey;
  /** null = empty slot. Otherwise the SaveBlob.header from the stored save. */
  header: SaveBlobHeader | null;
}

/** Underlying index format — one row per occupied slot. */
interface SaveIndex {
  rows: Array<{ key: SaveSlotKey; header: SaveBlobHeader }>;
}

const STORAGE_KEY_PREFIX = 'su:save:';
const INDEX_STORAGE_KEY = STORAGE_KEY_PREFIX + 'index';

function blobKey(slot: SaveSlotKey): string {
  return STORAGE_KEY_PREFIX + slot;
}

/**
 * Minimal Storage interface — matches the slice of the browser Storage API
 * we use, so an in-memory test adapter only has to implement these three
 * methods.
 */
export interface SaveStorageBackend {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function defaultBackend(): SaveStorageBackend {
  // Defer the lookup so module import doesn't reference localStorage in
  // environments that don't have it (Node, Vitest without jsdom).
  const ls = (globalThis as unknown as { localStorage?: SaveStorageBackend })
    .localStorage;
  if (!ls) {
    throw new Error(
      'No localStorage available — pass a SaveStorageBackend explicitly.',
    );
  }
  return ls;
}

export interface SaveStorage {
  /** All four slots, with `header: null` for empty ones. */
  listSlots(): SaveSlotEntry[];
  /** Read a slot. Returns null if the slot is empty or unparseable. */
  read(slot: SaveSlotKey): SaveBlob | null;
  /** Write a blob to a slot, updating the index. */
  write(slot: SaveSlotKey, blob: SaveBlob): void;
  /** Clear a slot and remove its index row. */
  remove(slot: SaveSlotKey): void;
}

/**
 * Build a SaveStorage backed by the given backend (default: browser localStorage).
 */
export function createSaveStorage(
  opts: { backend?: SaveStorageBackend } = {},
): SaveStorage {
  const backend = opts.backend ?? defaultBackend();

  function readIndex(): SaveIndex {
    const raw = backend.getItem(INDEX_STORAGE_KEY);
    if (raw === null) return { rows: [] };
    try {
      const parsed = JSON.parse(raw) as SaveIndex;
      if (!parsed || !Array.isArray(parsed.rows)) return { rows: [] };
      return parsed;
    } catch {
      // Corrupted index — treat as empty. Don't fail load: the actual blobs
      // may still be intact and re-readable; the index gets rebuilt on the
      // next write.
      return { rows: [] };
    }
  }

  function writeIndex(idx: SaveIndex): void {
    backend.setItem(INDEX_STORAGE_KEY, JSON.stringify(idx));
  }

  return {
    listSlots(): SaveSlotEntry[] {
      const idx = readIndex();
      const headerByKey = new Map<SaveSlotKey, SaveBlobHeader>();
      for (const row of idx.rows) headerByKey.set(row.key, row.header);
      return ALL_SLOT_KEYS.map((key) => ({
        key,
        header: headerByKey.get(key) ?? null,
      }));
    },
    read(slot: SaveSlotKey): SaveBlob | null {
      const raw = backend.getItem(blobKey(slot));
      if (raw === null) return null;
      try {
        return JSON.parse(raw) as SaveBlob;
      } catch {
        return null;
      }
    },
    write(slot: SaveSlotKey, blob: SaveBlob): void {
      backend.setItem(blobKey(slot), JSON.stringify(blob));
      const idx = readIndex();
      const without = idx.rows.filter((r) => r.key !== slot);
      without.push({ key: slot, header: blob.header });
      writeIndex({ rows: without });
    },
    remove(slot: SaveSlotKey): void {
      backend.removeItem(blobKey(slot));
      const idx = readIndex();
      const without = idx.rows.filter((r) => r.key !== slot);
      writeIndex({ rows: without });
    },
  };
}

/**
 * In-memory SaveStorageBackend for tests. Behaves exactly like a browser
 * Storage but lives in a Map; isolated per instance.
 */
export function createInMemoryBackend(): SaveStorageBackend {
  const map = new Map<string, string>();
  return {
    getItem: (k) => (map.has(k) ? map.get(k)! : null),
    setItem: (k, v) => {
      map.set(k, v);
    },
    removeItem: (k) => {
      map.delete(k);
    },
  };
}
